// src/components/UserList.tsx
import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Loader2, MailX, Pencil, UserCheck, UserMinus, X } from "lucide-react";
import { toast } from "react-toastify";
import { useUserContext } from "../contexts/UserContext";
import { useTenantCode } from "../services/hooks/useTenantCode";
import { useBranding } from "../contexts/BrandingContext";
import { switchableBranches } from "../utils/branches";
import { IconButton, IconButtonSlot } from "./common/IconButton";

interface User {
    id: string;
    firstName: string;
    lastName: string;
    emailAddresses: { emailAddress: string }[];
    publicMetadata: { role: string; tenantCode?: string; branchAccessMode?: string; branchCodes?: string[] };
    // B3: quitar a alguien de las personas de contacto de un centro NO borra su
    // cuenta, la banea (igual que al dar de baja a un cliente). El dato ya venía
    // en la respuesta —`getAllUsers` devuelve el objeto de Clerk tal cual— pero no
    // se pintaba, así que un usuario sin acceso se veía idéntico a uno normal.
    banned?: boolean;
    locked?: boolean;
    // Q1: desde que se invita en vez de crear con contraseña, el usuario de Clerk
    // no existe hasta que aceptan. `getAllUsers` devuelve también las invitaciones
    // pendientes con esta marca, para que dar de alta a alguien no parezca no
    // haber hecho nada.
    pendingInvitation?: boolean;
    invitationId?: string | null;
}

// Etiquetas y estilo por rol (mismo lenguaje visual que la tabla de clientes).
const ROLE_LABELS: Record<string, string> = {
    superadmin: 'Superadmin',
    admin: 'Administrador',
    user: 'Operador',
    client: 'Cliente',
};
const ROLE_BADGE: Record<string, string> = {
    superadmin: 'bg-purple-100 text-purple-800',
    admin: 'bg-amber-100 text-amber-800',
    user: 'bg-blue-100 text-blue-800',
    client: 'bg-green-100 text-green-800',
};
const roleLabel = (r?: string) => (r ? ROLE_LABELS[r] ?? r : 'Sin rol');

const BRANCH_MODE_LABELS: Record<string, string> = {
    single: 'Un centro',
    all: 'Todos los centros',
    subset: 'Varios centros',
};

const UserList = () => {
    const { getToken } = useAuth();
    const { user: currentUser } = useUser();
    const { users, setUsers } = useUserContext();
    const tenantCode = useTenantCode();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Edición de rol
    const [editing, setEditing] = useState<User | null>(null);
    const [newRole, setNewRole] = useState('user');
    // Feedback del PO: el acceso a sucursales se podía fijar SOLO al crear.
    const [newBranchMode, setNewBranchMode] = useState('single');
    const [newBranchCodes, setNewBranchCodes] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    // Baja / alta de acceso. La baja NO borra la cuenta: la deja sin poder
    // entrar, y se deshace desde la misma lista.
    const [accessTarget, setAccessTarget] = useState<User | null>(null);
    const [accessSaving, setAccessSaving] = useState(false);
    const { branding } = useBranding();
    const branchOptions = useMemo(() => switchableBranches(branding), [branding]);

    const isSuperAdmin = currentUser?.publicMetadata?.role === 'superadmin';

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const token = await getToken();
            const response = await fetch(`${import.meta.env.VITE_API_URL}/users`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "X-Active-Tenant-Code": tenantCode ?? "",
                },
            });
            if (!response.ok) throw new Error("Error fetching users");
            const data = await response.json();
            setUsers(Array.isArray(data) ? data : (data.data ?? []));
        } catch (err) {
            console.error("Error fetching users:", err);
            setError((err as Error).message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantCode]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const esMiUsuario = editing !== null && currentUser?.id === editing.id;

    const openEdit = (u: User) => {
        setEditing(u);
        setNewRole(u.publicMetadata?.role || 'user');
        setNewBranchMode(u.publicMetadata?.branchAccessMode || 'single');
        setNewBranchCodes(Array.isArray(u.publicMetadata?.branchCodes) ? u.publicMetadata.branchCodes : []);
    };

    const handleSaveRole = async () => {
        if (!editing) return;
        try {
            setSaving(true);
            const token = await getToken();
            const res = await fetch(`${import.meta.env.VITE_API_URL}/users/${editing.id}/role`, {
                method: 'PATCH',
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "X-Active-Tenant-Code": tenantCode ?? "",
                },
                body: JSON.stringify({
                    role: newRole,
                    branchAccessMode: newBranchMode,
                    branchCodes: newBranchMode === 'subset' ? newBranchCodes : [],
                    branchCode: newBranchMode === 'single' ? tenantCode : undefined,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || 'No se pudo actualizar el usuario');

            // Si te has editado a ti mismo, Clerk sigue sirviendo el
            // `publicMetadata` viejo desde su caché del navegador: el selector de
            // sucursales de la cabecera lo lee de ahí, así que seguía enseñando
            // los centros de antes hasta recargar la página a mano.
            if (currentUser?.id === editing.id) {
                await currentUser.reload();
            }

            toast.success('Usuario actualizado');
            setEditing(null);
            await fetchUsers();
        } catch (err) {
            toast.error((err as Error).message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleAccess = async () => {
        if (!accessTarget) return;
        const restoring = Boolean(accessTarget.banned);
        try {
            setAccessSaving(true);
            const token = await getToken();
            const res = await fetch(`${import.meta.env.VITE_API_URL}/users/${accessTarget.id}/access`, {
                method: 'PATCH',
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "X-Active-Tenant-Code": tenantCode ?? "",
                },
                body: JSON.stringify({ active: restoring }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || 'No se pudo cambiar el acceso');

            toast.success(data?.message || 'Acceso actualizado');
            setAccessTarget(null);
            await fetchUsers();
        } catch (err) {
            toast.error((err as Error).message);
        } finally {
            setAccessSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-10">
                <Loader2 className="animate-spin text-gray-400" />
            </div>
        );
    }

    if (error) {
        return <p className="text-red-600 bg-red-50 border border-red-200 rounded p-3">Error: {error}</p>;
    }

    return (
        <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acceso</th>
                            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                                    No hay usuarios registrados.
                                </td>
                            </tr>
                        ) : (
                            users.map((user) => {
                                const role = user.publicMetadata?.role;
                                const esYo = currentUser?.id === user.id;
                                return (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="font-medium text-gray-800">
                                                {user.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : '—'}
                                            </span>
                                            {esYo && <span className="ml-2 text-xs text-gray-400">(tú)</span>}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                                            {user.emailAddresses?.[0]?.emailAddress ?? '—'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_BADGE[role ?? ''] ?? 'bg-gray-100 text-gray-600'}`}>
                                                {roleLabel(role)}
                                            </span>
                                            {user.pendingInvitation && (
                                                <span
                                                    className="ml-2 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800"
                                                    title="Se le ha enviado una invitación; aún no ha entrado."
                                                >
                                                    Invitación pendiente
                                                </span>
                                            )}
                                            {(user.banned || user.locked) && (
                                                <span
                                                    className="ml-2 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold bg-red-100 text-red-800"
                                                    title={user.banned
                                                        ? 'Se le ha revocado el acceso; no puede iniciar sesión.'
                                                        : 'Cuenta bloqueada temporalmente por Clerk (demasiados intentos).'}
                                                >
                                                    Sin acceso
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                            {BRANCH_MODE_LABELS[user.publicMetadata?.branchAccessMode ?? 'single'] ?? '—'}
                                        </td>
                                        {/* Solo iconos: con el texto al lado, cada fila tenía un ancho
                                            distinto según la acción que tocara ("Quitar acceso" /
                                            "Cancelar invitación" / "Restaurar acceso") y la columna salía
                                            desalineada. El texto pasa al tooltip, que es lo que ya decía.
                                            Los huecos son de ancho fijo para que los iconos caigan siempre
                                            en la misma columna aunque falte una acción. */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-1">
                                                <IconButton
                                                    onClick={() => openEdit(user)}
                                                    disabled={Boolean(user.pendingInvitation)}
                                                    label={user.pendingInvitation
                                                        ? 'Todavía no ha aceptado la invitación'
                                                        : 'Editar rol y centros'}
                                                >
                                                    <Pencil size={18} />
                                                </IconButton>

                                                {/* La baja no borra la cuenta, la deja sin acceso, así que
                                                    se puede deshacer desde aquí mismo. En tu propia fila no
                                                    aparece: quitarte el acceso a ti mismo te echaría de la
                                                    pantalla desde la que se arregla (el backend lo rechaza
                                                    igualmente con un 409). */}
                                                {esYo ? (
                                                    <IconButtonSlot />
                                                ) : user.pendingInvitation ? (
                                                    <IconButton
                                                        tone="danger"
                                                        onClick={() => setAccessTarget(user)}
                                                        label="Cancelar la invitación enviada"
                                                    >
                                                        <MailX size={18} />
                                                    </IconButton>
                                                ) : user.banned ? (
                                                    <IconButton
                                                        tone="success"
                                                        onClick={() => setAccessTarget(user)}
                                                        label="Restaurar acceso"
                                                    >
                                                        <UserCheck size={18} />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton
                                                        tone="danger"
                                                        onClick={() => setAccessTarget(user)}
                                                        label="Quitar acceso: no podrá iniciar sesión"
                                                    >
                                                        <UserMinus size={18} />
                                                    </IconButton>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal de edición de rol */}
            {editing && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
                        <div className="flex justify-between items-center p-5 border-b">
                            <h3 className="text-lg font-bold">Editar rol</h3>
                            <button onClick={() => setEditing(null)} className="text-gray-500 hover:text-gray-700">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-5">
                            <p className="text-sm text-gray-600 mb-1">
                                {editing.firstName ? `${editing.firstName} ${editing.lastName ?? ''}`.trim() : editing.emailAddresses?.[0]?.emailAddress}
                            </p>
                            <p className="text-xs text-gray-400 mb-4">{editing.emailAddresses?.[0]?.emailAddress}</p>

                            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                            <select
                                value={newRole}
                                onChange={(e) => setNewRole(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="client">Cliente</option>
                                <option value="user">Operador</option>
                                <option value="admin">Administrador</option>
                                {/* Solo un superadmin puede otorgar superadmin (lo valida también el backend) */}
                                {isSuperAdmin && <option value="superadmin">Superadmin</option>}
                            </select>
                            <p className="text-xs text-gray-400 mt-2">
                                Promover a <strong>Administrador</strong> consume una plaza del límite de administradores del centro.
                            </p>

                            {/* Acceso a sucursales. Se podía elegir al crear el usuario y
                                nunca más: había que borrarlo y volver a crearlo para
                                cambiarlo. */}
                            {branchOptions.length > 1 && (
                                <div className="mt-4 border-t pt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Centros a los que accede</label>
                                    <select
                                        value={newBranchMode}
                                        onChange={(e) => setNewBranchMode(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="single">Solo este centro</option>
                                        <option value="subset">Varios centros</option>
                                        <option value="all">Todos los centros del grupo</option>
                                    </select>

                                    {newBranchMode === 'subset' && (
                                        <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                                            {branchOptions.map((b) => (
                                                <label key={b.code} className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        checked={newBranchCodes.includes(b.code)}
                                                        onChange={(e) => setNewBranchCodes((prev) => (
                                                            e.target.checked
                                                                ? [...prev, b.code]
                                                                : prev.filter((c) => c !== b.code)
                                                        ))}
                                                    />
                                                    <span>{b.name}{b.isRoot ? ' (central)' : ''}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}

                                    {esMiUsuario && (
                                        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                                            Te estás editando a ti mismo. Si te quitas permisos, los recuperas pidiéndoselo a otro administrador.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50">
                            <button
                                onClick={() => setEditing(null)}
                                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveRole}
                                disabled={saving || (newRole === editing.publicMetadata?.role && newBranchMode === (editing.publicMetadata?.branchAccessMode ?? 'single'))}
                                className="px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded disabled:opacity-50 flex items-center gap-2"
                            >
                                {saving && <Loader2 className="animate-spin" size={16} />}
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmación de baja / alta de acceso */}
            {accessTarget && (() => {
                const restoring = Boolean(accessTarget.banned);
                const isInvitation = Boolean(accessTarget.pendingInvitation);
                const nombre = accessTarget.firstName
                    ? `${accessTarget.firstName} ${accessTarget.lastName ?? ''}`.trim()
                    : accessTarget.emailAddresses?.[0]?.emailAddress;
                const titulo = isInvitation
                    ? 'Cancelar invitación'
                    : restoring ? 'Restaurar acceso' : 'Quitar acceso';

                return (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                        <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
                            <div className="flex justify-between items-center p-5 border-b">
                                <h3 className="text-lg font-bold">{titulo}</h3>
                                <button onClick={() => setAccessTarget(null)} className="text-gray-500 hover:text-gray-700">
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="p-5 text-sm text-gray-600 space-y-3">
                                <p>
                                    <strong className="text-gray-800">{nombre}</strong>
                                    <br />
                                    <span className="text-xs text-gray-400">{accessTarget.emailAddresses?.[0]?.emailAddress}</span>
                                </p>

                                {isInvitation ? (
                                    <p>
                                        Se anulará la invitación y el enlace que recibió dejará de funcionar.
                                        Para volver a darle acceso habrá que darle de alta de nuevo.
                                    </p>
                                ) : restoring ? (
                                    <p>Podrá volver a iniciar sesión con el mismo rol y los mismos centros que tenía.</p>
                                ) : (
                                    <p>
                                        No podrá iniciar sesión, pero <strong>la cuenta no se borra</strong>: seguirá
                                        en esta lista marcada como <em>Sin acceso</em> y podrás devolvérselo cuando
                                        quieras. Si es administrador, su plaza del límite del centro queda libre.
                                    </p>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 p-5 border-t bg-gray-50">
                                <button
                                    onClick={() => setAccessTarget(null)}
                                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleToggleAccess}
                                    disabled={accessSaving}
                                    className={`px-4 py-2 text-white rounded disabled:opacity-50 flex items-center gap-2 ${
                                        restoring ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                                    }`}
                                >
                                    {accessSaving && <Loader2 className="animate-spin" size={16} />}
                                    {titulo}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default UserList;
